import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { clampDrag, hoverIndexFor, moveItem, settleOffset, shiftFor, type Rect } from './slotReorder';

// Drag-to-reorder for a rail or list (axis 'y') or a row (axis 'x') of items. The dragged item follows the pointer on one
// axis only, its neighbours slide aside, and it glides into place on release. Mouse and pen drag straight away; touch
// needs a short press-and-hold first so a normal swipe still scrolls. Items are measured when the drag starts, so rows
// can differ in size. See slotReorder.ts for the maths.

type Axis = 'x' | 'y';

type UseSlotReorderOptions = {
  axis: Axis;
  /** Every item in the container, in display order (including any that can't be dragged but share the layout). */
  ids: string[];
  /** Called once on release with the full new order and the id that moved, only if the order changed. */
  onReorder: (orderedIds: string[], movedId: string) => void;
  /** The scrollable container: it is edge-scrolled while dragging and its scroll is folded into the drag distance. */
  scrollRef?: RefObject<HTMLElement | null>;
  /** How long a finger must rest on an item before it picks up. */
  holdMs?: number;
  /** How much the dragged item grows while it is held. */
  lift?: number;
};

type DragState = { id: string; startIndex: number; delta: number; hover: number; rects: Rect[]; settling: boolean };

type Pending = {
  id: string;
  index: number;
  startPos: number;
  startScroll: number;
  rects: Rect[];
  pointerType: string;
  activated: boolean;
  timer?: number;
};

const SLOT_EASE = '180ms cubic-bezier(0.2, 0, 0, 1)';
const MOUSE_SLOP_PX = 5;
const TOUCH_SLOP_PX = 8;
const EDGE_PX = 28;

export function useSlotReorder({ axis, ids, onReorder, scrollRef, holdMs = 300, lift = 1.06 }: UseSlotReorderOptions) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const pending = useRef<Pending | null>(null);
  const lastPos = useRef(0);
  const suppressClick = useRef(false);
  const settleTimer = useRef<number | undefined>(undefined);
  const raf = useRef(0);
  const itemEls = useRef<(HTMLElement | null)[]>([]);
  // Latest props for callbacks that outlive a render (timers, rAF).
  const live = useRef({ ids, onReorder });
  live.current = { ids, onReorder };

  const pos = (e: { clientX: number; clientY: number }) => (axis === 'x' ? e.clientX : e.clientY);
  const scrollPos = () => {
    const c = scrollRef?.current;
    return c ? (axis === 'x' ? c.scrollLeft : c.scrollTop) : 0;
  };

  const stopLoop = () => { cancelAnimationFrame(raf.current); raf.current = 0; };

  /** Where the dragged item and its hover slot are for the current pointer position. */
  const resolve = (p: Pending) => {
    const delta = clampDrag(lastPos.current - p.startPos + (scrollPos() - p.startScroll), p.index, p.rects);
    return { delta, hover: hoverIndexFor(delta, p.index, p.rects) };
  };

  const recompute = () => {
    const p = pending.current;
    if (!p?.activated) return;
    const { delta, hover } = resolve(p);
    setDrag((d) => (d && !d.settling ? { ...d, delta, hover } : d));
  };

  // While held near the container's edge, scroll it so items beyond the visible part can be reached.
  const tick = () => {
    const p = pending.current;
    if (!p?.activated) return;
    const c = scrollRef?.current;
    if (c) {
      const r = c.getBoundingClientRect();
      const lo = axis === 'x' ? r.left : r.top;
      const hi = axis === 'x' ? r.right : r.bottom;
      let v = 0;
      if (lastPos.current < lo + EDGE_PX) v = -Math.min(10, (lo + EDGE_PX - lastPos.current) / 3);
      else if (lastPos.current > hi - EDGE_PX) v = Math.min(10, (lastPos.current - (hi - EDGE_PX)) / 3);
      if (v) {
        if (axis === 'x') c.scrollLeft += v; else c.scrollTop += v;
        recompute();
      }
    }
    raf.current = requestAnimationFrame(tick);
  };

  const activate = () => {
    const p = pending.current;
    if (!p || p.activated) return;
    p.activated = true;
    suppressClick.current = true;
    document.body.dataset.reordering = '1'; // lets page-level swipe gestures (the drawers) stand down
    try { navigator.vibrate?.(10); } catch { /* noop */ }
    setDrag({ id: p.id, startIndex: p.index, delta: 0, hover: p.index, rects: p.rects, settling: false });
    stopLoop();
    raf.current = requestAnimationFrame(tick);
  };

  const reset = () => {
    const p = pending.current;
    if (p?.timer) window.clearTimeout(p.timer);
    pending.current = null;
    stopLoop();
    delete document.body.dataset.reordering;
  };

  const finish = (d: DragState) => {
    stopLoop();
    // Glide into the slot, then commit. Transitions only exist while a drag is active, so clearing it in the same render
    // as the new order doesn't animate a second time.
    setDrag({ ...d, delta: settleOffset(d.startIndex, d.hover, d.rects), settling: true });
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      if (d.hover !== d.startIndex) live.current.onReorder(moveItem(live.current.ids, d.startIndex, d.hover), d.id);
      setDrag(null);
      suppressClick.current = false;
    }, 180);
  };

  // Touch scrolling can only be stopped from a non-passive native listener, and only once an item is picked up.
  useEffect(() => {
    const block = (e: TouchEvent) => { if (pending.current?.activated && e.cancelable) e.preventDefault(); };
    document.addEventListener('touchmove', block, { passive: false });
    return () => {
      document.removeEventListener('touchmove', block);
      window.clearTimeout(settleTimer.current);
      cancelAnimationFrame(raf.current);
    };
  }, []);

  /** Every item's position along the axis right now, or null if some item isn't mounted. */
  const measure = (): Rect[] | null => {
    const n = live.current.ids.length;
    const rects: Rect[] = [];
    for (let i = 0; i < n; i++) {
      const el = itemEls.current[i];
      if (!el) return null;
      const r = el.getBoundingClientRect();
      rects.push(axis === 'x' ? { start: r.left, size: r.width } : { start: r.top, size: r.height });
    }
    return rects;
  };

  const itemProps = (index: number, id: string) => ({
    style: { userSelect: 'none', WebkitTouchCallout: 'none' } as CSSProperties,
    onContextMenu: (e: { preventDefault: () => void }) => { if (pending.current?.pointerType === 'touch') e.preventDefault(); },
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (drag || (e.pointerType === 'mouse' && e.button !== 0)) return;
      reset();
      const rects = measure();
      if (!rects) return;
      const p: Pending = { id, index, startPos: pos(e), startScroll: scrollPos(), rects, pointerType: e.pointerType, activated: false };
      lastPos.current = p.startPos;
      pending.current = p;
      if (e.pointerType === 'touch') p.timer = window.setTimeout(activate, holdMs);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const p = pending.current;
      if (!p || p.id !== id) return;
      lastPos.current = pos(e);
      const moved = Math.abs(lastPos.current - p.startPos);
      if (!p.activated) {
        if (p.pointerType === 'touch') { if (moved > TOUCH_SLOP_PX) reset(); return; } // finger moved first: it's a scroll
        if (moved < MOUSE_SLOP_PX) return; // a few pixels of slop so a normal click never becomes a drag
        activate();
      }
      recompute();
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
      const p = pending.current;
      reset();
      if (!p?.activated) return;
      const { delta, hover } = resolve(p);
      finish({ id: p.id, startIndex: p.index, delta, hover, rects: p.rects, settling: false });
    },
    onPointerCancel: () => {
      reset();
      suppressClick.current = false;
      setDrag(null);
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      // Keyboard reorder: Alt + the arrow keys for this axis moves the focused item one slot.
      const back = axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
      const fwd = axis === 'x' ? 'ArrowRight' : 'ArrowDown';
      if (!e.altKey || (e.key !== back && e.key !== fwd)) return;
      e.preventDefault();
      const to = index + (e.key === back ? -1 : 1);
      if (to >= 0 && to < live.current.ids.length) live.current.onReorder(moveItem(live.current.ids, index, to), id);
    },
  });

  /** Style for the element that wraps each item (the one that moves). */
  const itemStyle = (index: number, id: string): CSSProperties => {
    const dragging = drag?.id === id;
    const t = !drag ? 0 : dragging ? drag.delta : shiftFor(index, drag.startIndex, drag.hover, drag.rects);
    return {
      transform: drag ? `translate${axis.toUpperCase()}(${t}px)${dragging ? ` scale(${lift})` : ''}` : undefined,
      transition: drag && (!dragging || drag.settling) ? `transform ${SLOT_EASE}` : 'none',
      zIndex: dragging ? 20 : undefined,
    };
  };

  const itemRef = useCallback((index: number) => (el: HTMLElement | null) => { itemEls.current[index] = el; }, []);

  /** True once for the click that follows a drag, so dropping an item doesn't also open it. */
  const consumeClick = () => {
    if (!suppressClick.current) return false;
    suppressClick.current = false;
    return true;
  };

  return { dragId: drag?.id ?? null, itemProps, itemStyle, itemRef, consumeClick };
}
